import { Workbox } from 'workbox-window';
import i18n from '@/i18n';
import type {
  PWAConfig,
  PWAMetrics,
  PWANotification,
  CacheStrategy,
  PWAUpdateInfo,
} from '../types/pwa';

class PWAService {
  private workbox: Workbox | null = null;
  private registration: ServiceWorkerRegistration | null = null;
  private updateCallback: ((info: PWAUpdateInfo) => void) | null = null;

  private get config(): PWAConfig {
    return {
      name: i18n.t('common:pwa.manifest.name'),
      shortName: 'Xtreed',
      description: i18n.t('common:pwa.manifest.description'),
      themeColor: '#1f2937',
      backgroundColor: '#ffffff',
      display: 'standalone',
      orientation: 'portrait',
      categories: ['education', 'productivity', 'developer tools'],
      icons: [
        {
          src: '/icons/pwa-192x192.png',
          sizes: '192x192',
          type: 'image/png',
          purpose: 'maskable',
        },
        {
          src: '/icons/pwa-512x512.png',
          sizes: '512x512',
          type: 'image/png',
          purpose: 'maskable',
        },
        {
          src: '/icons/pwa-64x64.png',
          sizes: '64x64',
          type: 'image/png',
        },
      ],
    };
  }

  async initialize(): Promise<boolean> {
    try {
      if (import.meta.env.DEV) {
        return false;
      }

      if (!('serviceWorker' in navigator)) {
        console.warn('Service Workers not supported');
        return false;
      }

      this.workbox = new Workbox('/sw.js');

      // Listen for service worker updates
      this.workbox.addEventListener('waiting', () => {
        this.notifyUpdate({
          available: true,
          waiting: true,
          installing: false,
          current: 'unknown',
          waiting_version: 'new',
        });
      });

      this.workbox.addEventListener('controlling', () => {
        window.location.reload();
      });

      this.workbox.addEventListener('installed', (event) => {
        if (event.isUpdate) {
          this.notifyUpdate({
            available: false,
            waiting: false,
            installing: false,
            current: 'updated',
          });
        }
      });

      this.registration = (await this.workbox.register()) || null;

      // Set up background sync
      await this.setupBackgroundSync();

      return true;
    } catch (error) {
      console.error('PWA initialization failed:', error);
      return false;
    }
  }

  async checkForUpdates(): Promise<PWAUpdateInfo> {
    if (!this.registration) {
      throw new Error(i18n.t('common:pwa.errors.serviceWorkerNotRegistered'));
    }

    try {
      await this.registration.update();

      const info: PWAUpdateInfo = {
        available: Boolean(this.registration.waiting),
        waiting: Boolean(this.registration.waiting),
        installing: Boolean(this.registration.installing),
        current: 'v1.0.0', // This would come from your build process
      };

      return info;
    } catch (error) {
      console.error('Update check failed:', error);
      throw error;
    }
  }

  async skipWaiting(): Promise<void> {
    if (!this.workbox) {
      throw new Error(i18n.t('common:pwa.errors.workboxNotInitialized'));
    }

    this.workbox.messageSkipWaiting();
  }

  async showNotification(notification: PWANotification): Promise<void> {
    if (!('Notification' in window)) {
      throw new Error(i18n.t('common:pwa.errors.notificationsNotSupported'));
    }

    if (Notification.permission !== 'granted') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        throw new Error(
          i18n.t('common:pwa.errors.notificationPermissionDenied'),
        );
      }
    }

    if (this.registration) {
      await this.registration.showNotification(notification.title, {
        body: notification.message,
        icon: notification.icon || '/icons/pwa-192x192.png',
        badge: notification.badge || '/icons/pwa-64x64.png',
        tag: notification.tag,
        data: notification.data,
        requireInteraction: notification.requireInteraction,
        silent: notification.silent,
      });
    } else {
      // Fallback to regular notification
      new Notification(notification.title, {
        body: notification.message,
        icon: notification.icon || '/icons/pwa-192x192.png',
      });
    }
  }

  async getMetrics(): Promise<PWAMetrics> {
    // This would integrate with your analytics service
    return {
      totalInstalls: this.getStoredMetric('total_installs', 0),
      dailyActiveUsers: this.getStoredMetric('daily_active_users', 0),
      retentionRate: this.getStoredMetric('retention_rate', 0),
      offlineUsage: this.getStoredMetric('offline_usage', 0),
      updateSuccessRate: this.getStoredMetric('update_success_rate', 100),
      averageInstallTime: this.getStoredMetric('avg_install_time', 0),
      platformBreakdown: {
        android: this.getStoredMetric('platform_android', 0),
        ios: this.getStoredMetric('platform_ios', 0),
        desktop: this.getStoredMetric('platform_desktop', 0),
      },
      featureUsage: {
        offline_mode: this.getStoredMetric('feature_offline', 0),
        push_notifications: this.getStoredMetric('feature_notifications', 0),
        background_sync: this.getStoredMetric('feature_background_sync', 0),
      },
    };
  }

  async updateCacheStrategy(strategies: CacheStrategy[]): Promise<void> {
    if (!this.registration) {
      throw new Error(i18n.t('common:pwa.errors.serviceWorkerNotRegistered'));
    }

    // Send cache strategies to service worker
    navigator.serviceWorker.controller?.postMessage({
      type: 'UPDATE_CACHE_STRATEGIES',
      strategies,
    });
  }

  async clearCache(cacheNames?: string[]): Promise<void> {
    const allCacheNames = await caches.keys();
    const cachesToDelete = cacheNames || allCacheNames;

    await Promise.all(
      cachesToDelete.map((cacheName) => caches.delete(cacheName)),
    );
  }

  async getCacheSize(): Promise<number> {
    if (!('storage' in navigator && 'estimate' in navigator.storage)) {
      return 0;
    }

    try {
      const estimate = await navigator.storage.estimate();
      return estimate.usage || 0;
    } catch (error) {
      console.error('Failed to get cache size:', error);
      return 0;
    }
  }

  isOnline(): boolean {
    return navigator.onLine;
  }

  onUpdateAvailable(callback: (info: PWAUpdateInfo) => void): void {
    this.updateCallback = callback;
  }

  private notifyUpdate(info: PWAUpdateInfo): void {
    if (this.updateCallback) {
      this.updateCallback(info);
    }
  }

  private async setupBackgroundSync(): Promise<void> {
    if (!(
      'serviceWorker' in navigator &&
      'sync' in window.ServiceWorkerRegistration.prototype
    )) {
      console.warn('Background Sync not supported');
      return;
    }

    // Register background sync
    if (this.registration && 'sync' in this.registration) {
      try {
        await (this.registration as any).sync.register('background-sync');
      } catch (error) {
        console.error('Background sync registration failed:', error);
      }
    }
  }

  private getStoredMetric(key: string, defaultValue: number): number {
    const value = localStorage.getItem(`pwa_metric_${key}`);
    return value ? parseInt(value, 10) : defaultValue;
  }

  private setStoredMetric(key: string, value: number): void {
    localStorage.setItem(`pwa_metric_${key}`, value.toString());
  }

  async trackInstall(): Promise<void> {
    const currentInstalls = this.getStoredMetric('total_installs', 0);
    this.setStoredMetric('total_installs', currentInstalls + 1);

    // Track platform
    const platform = this.detectPlatform();
    const platformKey = `platform_${platform}`;
    const platformCount = this.getStoredMetric(platformKey, 0);
    this.setStoredMetric(platformKey, platformCount + 1);
  }

  private detectPlatform(): string {
    const userAgent = navigator.userAgent.toLowerCase();

    if (/android/.test(userAgent)) {
      return 'android';
    } else if (/iphone|ipad|ipod/.test(userAgent)) {
      return 'ios';
    } else {
      return 'desktop';
    }
  }

  getConfig(): PWAConfig {
    return { ...this.config };
  }

  async generateManifest(): Promise<object> {
    return {
      name: this.config.name,
      short_name: this.config.shortName,
      description: this.config.description,
      theme_color: this.config.themeColor,
      background_color: this.config.backgroundColor,
      display: this.config.display,
      orientation: this.config.orientation,
      scope: '/',
      start_url: '/',
      categories: this.config.categories,
      icons: this.config.icons,
      shortcuts: [
        {
          name: i18n.t('common:pwa.manifest.shortcuts.courses.name'),
          short_name: i18n.t('common:pwa.manifest.shortcuts.courses.shortName'),
          description: i18n.t(
            'common:pwa.manifest.shortcuts.courses.description',
          ),
          url: '/courses',
          icons: [{ src: '/icons/courses-96x96.png', sizes: '96x96' }],
        },
        {
          name: i18n.t('common:pwa.manifest.shortcuts.achievements.name'),
          short_name: i18n.t(
            'common:pwa.manifest.shortcuts.achievements.shortName',
          ),
          description: i18n.t(
            'common:pwa.manifest.shortcuts.achievements.description',
          ),
          url: '/achievements',
          icons: [{ src: '/icons/achievements-96x96.png', sizes: '96x96' }],
        },
      ],
    };
  }
}

export const pwaService = new PWAService();
export default pwaService;
